<template>
  <div class="px-4 py-4">
    <component
      :title="slideoverTitle"
      :is="slideoverComponent"
      :visible="slideoverVisible"
      v-on:toggle-slideover="toggleSlideover"
    />

    <div class="flex flex-row items-center justify-between mb-3">
      <div class="font-bold text-gray-800">{{ summary && summary.status }}</div>
      <ActionsMenu v-if="canShowActions">
        <ActionsItem
          @click="toggleSlideover('EditShippingSlideoverVBI', 'Edit Shipping')"
        >
          Edit Shipping
        </ActionsItem>
        <ActionsItem
          @click="toggleSlideover('IssueRefundSlideover', 'Issue Refund')"
        >
          Issue Refund
        </ActionsItem>
        <ActionsItem
          @click="
            toggleSlideover('IssueDiscountSlideover', 'Issue Discount Code')
          "
        >
          Issue Discount Code
        </ActionsItem>
      </ActionsMenu>
    </div>

    <div v-if="!loaded" class="flex items-center space-x-2 text-gray-500">
      <span>Loading totals from Shopify</span>
      <LoadingSpinner class="text-gray-400" />
    </div>

    <div v-else-if="notInShopify" class="text-gray-500">
      Totals aren't available because this order isn't in Shopify.
    </div>

    <HttpError v-else-if="error" :error="error" />

    <div v-else-if="summary" class="border border-gray-300 rounded-lg">
      <!-- Original order (only once the order has refunds) -->
      <div v-if="summary.original" class="section">
        <div class="row">
          <div class="label">Original order</div>
          <div class="description">{{ summary.original.date }}</div>
          <div class="amount">{{ summary.original.amount | currency }}</div>
        </div>
      </div>

      <!-- Charges -->
      <div class="section">
        <div class="row">
          <div class="label">Subtotal</div>
          <div class="description">{{ summary.subtotal.description }}</div>
          <div class="amount">{{ summary.subtotal.amount | currency }}</div>
        </div>
        <!-- Subtotal and shipping are after discounts, so these rows are for reference and aren't subtracted -->
        <div
          v-for="(discount, index) in summary.discounts"
          :key="`discount-${index}`"
          class="row text-gray-500"
        >
          <div class="label">
            <template v-if="index === 0">
              {{ summary.discounts.length > 1 ? "Discounts" : "Discount" }}
            </template>
          </div>
          <div class="description">{{ discount.title }}</div>
          <div class="amount">({{ discount.amount | currency }})</div>
        </div>
        <div v-if="summary.discounts.length" class="row text-xs text-gray-500">
          <div class="label"></div>
          <div class="description">
            Discounts above are already included in the subtotal.
          </div>
          <div class="amount"></div>
        </div>
        <div v-if="summary.shipping" class="row">
          <div class="label">Shipping</div>
          <div class="description">{{ summary.shipping.description }}</div>
          <div class="amount">{{ summary.shipping.amount | currency }}</div>
        </div>
        <div
          v-if="summary.taxes.amount || summary.taxes.lines.length"
          class="row"
        >
          <div class="label">Taxes</div>
          <div class="description">
            <template v-if="summary.taxes.lines.length === 1">
              {{ summary.taxes.lines[0].description }}
            </template>
            <button
              v-else-if="summary.taxes.lines.length > 1"
              type="button"
              class="focus:outline-none hover:underline"
              @click="showTaxDetails = !showTaxDetails"
            >
              Tax details {{ showTaxDetails ? "▴" : "▾" }}
            </button>
          </div>
          <div class="amount">{{ summary.taxes.amount | currency }}</div>
        </div>
        <template v-if="showTaxDetails && summary.taxes.lines.length > 1">
          <div
            v-for="(taxLine, index) in summary.taxes.lines"
            :key="`tax-${index}`"
            class="row text-gray-500"
          >
            <div class="label"></div>
            <div class="description">{{ taxLine.description }}</div>
            <div class="amount">{{ taxLine.amount | currency }}</div>
          </div>
        </template>
        <div class="row font-bold">
          <div class="label">Total</div>
          <div class="description"></div>
          <div class="amount">{{ summary.total | currency }}</div>
        </div>
      </div>

      <!-- Payments -->
      <div class="section">
        <div class="row">
          <div class="label">Paid</div>
          <div class="description"></div>
          <div class="amount">{{ summary.paid | currency }}</div>
        </div>
        <div
          v-for="(refund, index) in summary.refunds"
          :key="`refund-${index}`"
          class="row"
        >
          <div class="label">
            <template v-if="index === 0">Refunded</template>
          </div>
          <div class="description">{{ refund.description }}</div>
          <div class="amount">-{{ refund.amount | currency }}</div>
        </div>
        <div v-if="summary.netPayment !== null" class="row font-bold">
          <div class="label">Net payment</div>
          <div class="description"></div>
          <div class="amount">{{ summary.netPayment | currency }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moneyFormatter from "@/mixins/moneyFormatter";
import shopifyOrderSummary from "@/mixins/shopifyOrderSummary";
import EditShippingSlideoverVBI from "./slideovers/VBI/EditShippingVBI.vue";
import IssueDiscountSlideover from "./slideovers/IssueDiscount.vue";
import IssueRefundSlideover from "./slideovers/VBI/IssueRefund.vue";

export default {
  name: "OrderShopifyTotals",
  components: {
    EditShippingSlideoverVBI,
    IssueDiscountSlideover,
    IssueRefundSlideover
  },
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  filters: {
    currency: val => moneyFormatter.format(val)
  },
  data() {
    return {
      showTaxDetails: false,
      slideoverVisible: false,
      slideoverComponent: null,
      slideoverTitle: ""
    };
  },
  computed: {
    loaded() {
      return this.$store.getters["orders/get"]("shopifyOrderLoaded");
    },
    error() {
      return this.$store.getters["orders/get"]("shopifyOrderError");
    },
    notInShopify() {
      return this.error?.response?.status === 404;
    },
    summary() {
      return shopifyOrderSummary.build(
        this.$store.getters["orders/get"]("shopifyOrder")
      );
    },
    canShowActions() {
      if (this.order.status?.Status === "Unprocessed") return false;

      return this.orderWasPlacedOnShopify && !this.orderIsCancelled;
    },
    orderWasPlacedOnShopify() {
      return [22, 23, 31, 32].indexOf(Number(this.order.CartID)) > -1;
    },
    orderIsCancelled() {
      return Number(this.order.Cancelled);
    }
  },
  methods: {
    toggleSlideover(component, title) {
      this.slideoverComponent = component;
      this.slideoverTitle = title;

      // TODO better way to handle this, I'm using a timeout to allow for the proper transition effect
      setTimeout(() => {
        this.slideoverVisible = !this.slideoverVisible;
      }, 3);
    }
  }
};
</script>

<style scoped lang="scss">
.section {
  @apply px-4 py-2;
}

.section + .section {
  @apply border-t border-gray-300;
}

.row {
  @apply flex flex-row py-1;
}

// Fixed label/amount widths keep columns aligned; description absorbs width and wraps
.label {
  @apply w-32 flex-shrink-0 pr-4 whitespace-no-wrap;
}

.description {
  @apply flex-1 min-w-0 pr-4 break-words text-gray-700;
}

.amount {
  @apply w-32 flex-shrink-0 text-right whitespace-no-wrap;
}
</style>
